import { FilterState } from "./types";

/**
 * Parses a search query string to extract filter parameters
 */
export const parseQueryToFilters = (query: string): FilterState => {
  const filters: FilterState = {};

  // Extract price range
  const minPriceMatch = query.match(/priced from \$([0-9,]+)/);
  const maxPriceMatch = query.match(/priced under \$([0-9,]+)/);
  const priceBetweenMatch = query.match(
    /priced between \$([0-9,]+) to \$([0-9,]+)/
  );

  if (priceBetweenMatch) {
    filters.priceRange = {
      min: parseInt(priceBetweenMatch[1].replace(/,/g, ""), 10),
      max: parseInt(priceBetweenMatch[2].replace(/,/g, ""), 10),
    };
  } else {
    if (minPriceMatch || maxPriceMatch) {
      filters.priceRange = {};
      if (minPriceMatch) {
        filters.priceRange.min = parseInt(
          minPriceMatch[1].replace(/,/g, ""),
          10
        );
      }
      if (maxPriceMatch) {
        filters.priceRange.max = parseInt(
          maxPriceMatch[1].replace(/,/g, ""),
          10
        );
      }
    }
  }

  // Extract sort
  const sortMatch = query.match(/sort by (highest|lowest) price/);
  if (sortMatch) {
    filters.priceSort = sortMatch[1] === "highest" ? "desc" : "asc";
  }

  // Extract bedrooms
  const bedroomMatch = query.match(/(\d+)(\+)? bedroom/);
  if (bedroomMatch) {
    filters.bedrooms = bedroomMatch[1] + (bedroomMatch[2] || "");
  }

  // Extract bathrooms
  const bathroomMatch = query.match(/(\d+)(\+)? bathroom/);
  if (bathroomMatch) {
    filters.bathrooms = bathroomMatch[1] + (bathroomMatch[2] || "");
  }

  // Extract square footage
  const sqftBetweenMatch = query.match(/between ([\d,]+) and ([\d,]+) sqft/i);
  const sqftFromMatch = query.match(/from ([\d,]+) sqft/i);
  const sqftUnderMatch = query.match(/under ([\d,]+) sqft/i);

  if (sqftBetweenMatch) {
    filters.sqftRange = {
      min: parseInt(sqftBetweenMatch[1].replace(/,/g, ""), 10),
      max: parseInt(sqftBetweenMatch[2].replace(/,/g, ""), 10),
    };
  } else {
    if (sqftFromMatch || sqftUnderMatch) {
      filters.sqftRange = {};
      if (sqftFromMatch) {
        filters.sqftRange.min = parseInt(
          sqftFromMatch[1].replace(/,/g, ""),
          10
        );
      }
      if (sqftUnderMatch) {
        filters.sqftRange.max = parseInt(
          sqftUnderMatch[1].replace(/,/g, ""),
          10
        );
      }
    }
  }

  // Extract property features
  const featureMatch = query.match(
    /with\s+((?:waterfront|pool|basement|garage|city view|mountain view|park view|water view)(?:\s*,\s*(?:waterfront|pool|basement|garage|city view|mountain view|park view|water view))*(?:\s*,?\s*and\s*(?:waterfront|pool|basement|garage|city view|mountain view|park view|water view))?)/i
  );
  filters.features = {
    waterfront: featureMatch ? /\bwaterfront\b/i.test(featureMatch[1]) : false,
    pool: featureMatch ? /\bpool\b/i.test(featureMatch[1]) : false,
    basement: featureMatch ? /\bbasement\b/i.test(featureMatch[1]) : false,
    garage: featureMatch ? /\bgarage\b/i.test(featureMatch[1]) : false,
    cityView: featureMatch ? /\bcity view\b/i.test(featureMatch[1]) : false,
    mountainView: featureMatch
      ? /\bmountain view\b/i.test(featureMatch[1])
      : false,
    parkView: featureMatch ? /\bpark view\b/i.test(featureMatch[1]) : false,
    waterView: featureMatch ? /\bwater view\b/i.test(featureMatch[1]) : false,
  };

  return filters;
};

/**
 * Removes filter-related text from a query string
 */
export const cleanQueryFromFilters = (query: string): string => {
  return query
    .replace(/priced from \$[\d,]+/g, "")
    .replace(/priced under \$[\d,]+/g, "")
    .replace(/priced between \$[\d,]+ to \$[\d,]+/g, "")
    .replace(/sort by (highest|lowest) price/g, "")
    .replace(/\d+\+? bedroom with \d+\+? bathroom/g, "")
    .replace(/\d+\+? bedroom/g, "")
    .replace(/\d+\+? bathroom/g, "")
    .replace(/\b(active|pending|sold)\s+property\b/gi, "")
    .replace(/between [\d,]+ and [\d,]+ sqft/gi, "")
    .replace(/from [\d,]+ sqft/gi, "")
    .replace(/under [\d,]+ sqft/gi, "")
    .replace(
      /with\s+(waterfront|pool|basement|garage|city view|mountain view|park view|water view)(?:\s*,\s*(waterfront|pool|basement|garage|city view|mountain view|park view|water view))*(?:\s*,?\s*and\s+(waterfront|pool|basement|garage|city view|mountain view|park view|water view))?/gi,
      ""
    )
    .trim()
    .replace(/\s+/g, " ");
};

/**
 * Builds a filter query string from filter state
 */
export const buildFilterQuery = (
  currentQuery: string,
  tempFilters: FilterState
): string => {
  const parts = [];
  const cleanedQuery = cleanQueryFromFilters(currentQuery || "");

  // Add price range
  if (tempFilters.priceRange?.min && tempFilters.priceRange?.max) {
    parts.push(
      `priced between $${tempFilters.priceRange.min.toLocaleString()} to $${tempFilters.priceRange.max.toLocaleString()}`
    );
  } else {
    if (tempFilters.priceRange?.min)
      parts.push(`priced from $${tempFilters.priceRange.min.toLocaleString()}`);
    if (tempFilters.priceRange?.max)
      parts.push(
        `priced under $${tempFilters.priceRange.max.toLocaleString()}`
      );
  }

  // Add sort
  if (tempFilters.priceSort) {
    parts.push(
      `sort by ${tempFilters.priceSort === "desc" ? "highest" : "lowest"} price`
    );
  }

  // Add bedrooms and bathrooms
  const bedPart = tempFilters.bedrooms
    ? `${tempFilters.bedrooms.replace("+", "")}${
        !tempFilters.useExactMatchBeds && tempFilters.bedrooms.includes("+")
          ? "+"
          : ""
      } bedroom`
    : "";
  const bathPart = tempFilters.bathrooms
    ? `${tempFilters.bathrooms.replace("+", "")}${
        !tempFilters.useExactMatchBaths && tempFilters.bathrooms.includes("+")
          ? "+"
          : ""
      } bathroom`
    : "";

  if (bedPart && bathPart) {
    parts.push(`${bedPart} with ${bathPart}`);
  } else {
    if (bedPart) parts.push(bedPart);
    if (bathPart) parts.push(bathPart);
  }

  // Add square footage
  if (tempFilters.sqftRange?.min && tempFilters.sqftRange?.max) {
    parts.push(
      `between ${tempFilters.sqftRange.min.toLocaleString()} and ${tempFilters.sqftRange.max.toLocaleString()} sqft`
    );
  } else {
    if (tempFilters.sqftRange?.min)
      parts.push(`from ${tempFilters.sqftRange.min.toLocaleString()} sqft`);
    if (tempFilters.sqftRange?.max)
      parts.push(`under ${tempFilters.sqftRange.max.toLocaleString()} sqft`);
  }

  // Add property features
  const features = [];
  if (tempFilters.features?.waterfront) features.push("waterfront");
  if (tempFilters.features?.pool) features.push("pool");
  if (tempFilters.features?.basement) features.push("basement");
  if (tempFilters.features?.garage) features.push("garage");
  if (tempFilters.features?.cityView) features.push("city view");
  if (tempFilters.features?.mountainView) features.push("mountain view");
  if (tempFilters.features?.parkView) features.push("park view");
  if (tempFilters.features?.waterView) features.push("water view");

  // Filter out empty parts and join with proper spacing
  const validParts = parts.filter(Boolean);
  const query =
    validParts.length > 0
      ? `${cleanedQuery} ${validParts.join(" ")}`
      : cleanedQuery;

  // Add features at the end with proper formatting
  if (features.length > 0) {
    if (features.length === 1) {
      return `${query} with ${features[0]}`;
    } else if (features.length === 2) {
      return `${query} with ${features[0]} and ${features[1]}`;
    } else {
      const lastFeature = features[features.length - 1];
      const otherFeatures = features.slice(0, -1);
      return `${query} with ${otherFeatures.join(", ")}, and ${lastFeature}`;
    }
  }
  return query;
};
